import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { closeOutline, linkOutline, logoFacebook, playCircleOutline } from "ionicons/icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import CityUpdatesContent from "./CityUpdatesContent";
import "swiper/css";
import "swiper/css/pagination";
import "../pages/CityUpdates.css";

const FUNCTIONS_BASE = "https://us-central1-bacolod-ai-assistant.cloudfunctions.net";
const POST_PREVIEW_LENGTH = 280;

type UpdatePost = {
  id: string;
  pageId: string;
  pageName: string;
  message: string;
  story: string;
  imageUrl: string | null;
  media: Array<{
    type: "image" | "video";
    url: string | null;
    thumbnailUrl: string | null;
  }>;
  permalinkUrl: string | null;
  createdTimeMs: number;
  createdTimeIso: string | null;
};

const apiUrl = (name: string) => `${FUNCTIONS_BASE}/${name}`;

const formatTimestamp = (value?: string | null, ms?: number) => {
  const date = value ? new Date(value) : ms ? new Date(ms) : null;
  if (!date || Number.isNaN(date.getTime())) return "Just now";

  return new Intl.DateTimeFormat("en-PH", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
const TRAILING_PUNCTUATION = /[),.!?]+$/;

const getDisplayPostText = (post: UpdatePost) =>
  post.message || post.story || "New update posted on this Bacolod source.";

const getPreviewText = (text: string) => {
  if (text.length <= POST_PREVIEW_LENGTH) {
    return text;
  }

  const sliced = text.slice(0, POST_PREVIEW_LENGTH);
  const lastWhitespaceIndex = Math.max(
    sliced.lastIndexOf(" "),
    sliced.lastIndexOf("\n"),
    sliced.lastIndexOf("\t"),
  );

  const previewBase = lastWhitespaceIndex > POST_PREVIEW_LENGTH * 0.65
    ? sliced.slice(0, lastWhitespaceIndex)
    : sliced;

  return `${previewBase.trimEnd()}...`;
};

const shouldAllowCaptionExpand = (text: string) => {
  const normalized = text.trim();
  return normalized.length > 88 || normalized.includes("\n");
};

const renderLinkedText = (text: string) => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let matchIndex = 0;
  const regex = new RegExp(URL_PATTERN.source, URL_PATTERN.flags);

  let match = regex.exec(text);
  while (match) {
    const rawMatch = match[0];
    const start = match.index ?? 0;
    const end = start + rawMatch.length;

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    const trailing = rawMatch.match(TRAILING_PUNCTUATION)?.[0] || "";
    const cleanUrl = trailing ? rawMatch.slice(0, -trailing.length) : rawMatch;
    const href = cleanUrl.startsWith("http") ? cleanUrl : `https://${cleanUrl}`;

    nodes.push(
      <a
        key={`inline-link-${matchIndex}`}
        className="updates-post-inline-link"
        href={href}
        target="_blank"
        rel="noreferrer"
      >
        {cleanUrl}
      </a>
    );

    if (trailing) {
      nodes.push(trailing);
    }

    lastIndex = end;
    matchIndex += 1;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length ? nodes : text;
};

const getPostMedia = (post: UpdatePost) => {
  if (Array.isArray(post.media) && post.media.length) {
    return post.media;
  }

  if (post.imageUrl) {
    return [
      {
        type: "image" as const,
        url: post.imageUrl,
        thumbnailUrl: post.imageUrl,
      },
    ];
  }

  return [];
};

const isLikelyPlayableVideoUrl = (value?: string | null) => {
  if (!value) return false;
  return /\.(mp4|mov|m4v|webm|m3u8)(\?|#|$)/i.test(value);
};

const getMediaActionUrl = (
  mediaItem: UpdatePost["media"][number],
  permalinkUrl: string | null,
) => {
  if (mediaItem.type === "video" && !isLikelyPlayableVideoUrl(mediaItem.url)) {
    return permalinkUrl || mediaItem.url || "#";
  }

  return mediaItem.url || permalinkUrl || "#";
};

type GalleryState = {
  postId: string;
  pageName: string;
  message: string;
  items: UpdatePost["media"];
  initialIndex: number;
  permalinkUrl: string | null;
} | null;

const AutoPlayFeedVideo: React.FC<{
  src: string;
  poster?: string | null;
}> = ({ src, poster }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const playVideo = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => undefined);
      }
    };

    const pauseVideo = () => {
      video.pause();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseVideo();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      playVideo();

      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        pauseVideo();
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (document.hidden) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.65) {
          playVideo();
          return;
        }

        pauseVideo();
      },
      {
        threshold: [0.15, 0.4, 0.65, 0.9],
      },
    );

    observer.observe(video);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      observer.disconnect();
      pauseVideo();
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      className="updates-post-video-player"
      controls
      muted
      playsInline
      autoPlay
      preload="metadata"
      poster={poster || undefined}
    >
      <source src={src} />
    </video>
  );
};

export interface CityUpdatesFeedHandle {
  reload: () => Promise<void>;
}

const CityUpdatesFeed = forwardRef<CityUpdatesFeedHandle>((_props, ref) => {
  const [updates, setUpdates] = useState<UpdatePost[]>([]);
  const [isLoadingUpdates, setIsLoadingUpdates] = useState(true);
  const [updatesError, setUpdatesError] = useState("");
  const [expandedPosts, setExpandedPosts] = useState<Record<string, boolean>>({});
  const [galleryState, setGalleryState] = useState<GalleryState>(null);
  const [isGalleryCaptionExpanded, setIsGalleryCaptionExpanded] = useState(false);
  const swiperRef = useRef<any>(null);

  const loadUpdates = useCallback(async () => {
    setIsLoadingUpdates(true);
    setUpdatesError("");

    try {
      const response = await fetch(apiUrl("facebookGetUpdates"));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to load updates.");
      }

      setUpdates(Array.isArray(data.updates) ? data.updates : []);
    } catch (error) {
      setUpdatesError(error instanceof Error ? error.message : "Failed to load updates.");
    } finally {
      setIsLoadingUpdates(false);
    }
  }, []);

  useEffect(() => {
    loadUpdates();
  }, [loadUpdates]);

  useImperativeHandle(ref, () => ({ reload: loadUpdates }), [loadUpdates]);

  const togglePostExpanded = (postId: string) => {
    setExpandedPosts((current) => ({
      ...current,
      [postId]: !current[postId],
    }));
  };

  const openGallery = (post: UpdatePost, items: UpdatePost["media"], initialIndex: number) => {
    setIsGalleryCaptionExpanded(false);
    setGalleryState({
      postId: post.id,
      pageName: post.pageName,
      message: getDisplayPostText(post),
      items,
      initialIndex,
      permalinkUrl: post.permalinkUrl,
    });
  };

  return (
    <>
      {(updates.length > 0 || isLoadingUpdates) && (
        <div className="updates-live-hero">
          <div className="updates-live-pill">
            <IonIcon icon={logoFacebook} />
            <span>Live city feed</span>
          </div>
        </div>
      )}

      {updatesError && <div className="updates-error">{updatesError}</div>}

      {isLoadingUpdates ? (
        <div className="updates-loading-state">Loading recent updates...</div>
      ) : updates.length > 0 ? (
        <div className="updates-feed-list">
          {updates.map((post) => {
            const postText = getDisplayPostText(post);
            const postMedia = getPostMedia(post);
            const isLongPost = postText.length > POST_PREVIEW_LENGTH;
            const isExpanded = Boolean(expandedPosts[post.id]);
            const previewText = isExpanded || !isLongPost ? postText : getPreviewText(postText);

            return (
              <article key={post.id} className="updates-post-card">
                <div className="updates-post-top">
                  <div className="updates-post-meta">
                    <span className="updates-post-source">{post.pageName}</span>
                    <span className="updates-post-time">{formatTimestamp(post.createdTimeIso, post.createdTimeMs)}</span>
                  </div>
                  {post.permalinkUrl && (
                    <a className="updates-post-link" href={post.permalinkUrl} target="_blank" rel="noreferrer">
                      <IonIcon icon={linkOutline} />
                      <span>Open</span>
                    </a>
                  )}
                </div>

                <p className="updates-post-copy">{renderLinkedText(previewText)}</p>

                {isLongPost && (
                  <button
                    type="button"
                    className="updates-post-expand"
                    onClick={() => togglePostExpanded(post.id)}
                  >
                    {isExpanded ? "See less" : "See more"}
                  </button>
                )}

                {postMedia.length > 0 && (() => {
                  const hasMultipleMedia = postMedia.length > 1;
                  const firstMedia = postMedia[0];
                  const isSinglePlayableVideo =
                    !hasMultipleMedia &&
                    firstMedia?.type === "video" &&
                    isLikelyPlayableVideoUrl(firstMedia.url);

                  if (isSinglePlayableVideo) {
                    return (
                      <div className="updates-post-video-player-wrap">
                        {firstMedia.url && (
                          <AutoPlayFeedVideo
                            src={firstMedia.url}
                            poster={firstMedia.thumbnailUrl}
                          />
                        )}
                      </div>
                    );
                  }

                  return (
                    <div className={`updates-post-media updates-post-media--count-${Math.min(postMedia.length, 4)} ${hasMultipleMedia ? "updates-post-media--scroller" : ""}`}>
                      {postMedia.map((mediaItem, mediaIndex) => {
                        const href = getMediaActionUrl(mediaItem, post.permalinkUrl);
                        const previewUrl = mediaItem.thumbnailUrl || mediaItem.url;
                        const isVideo = mediaItem.type === "video";

                        const handleTileClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                          event.preventDefault();

                          if (hasMultipleMedia || !isVideo || isLikelyPlayableVideoUrl(mediaItem.url)) {
                            openGallery(post, postMedia, mediaIndex);
                            return;
                          }

                          if (href && href !== "#") {
                            window.open(href, "_blank", "noopener,noreferrer");
                          }
                        };

                        return (
                          <button
                            key={`${post.id}-media-${mediaIndex}`}
                            type="button"
                            className={`updates-post-media-tile ${isVideo ? "updates-post-media-tile-video" : ""}`}
                            onClick={handleTileClick}
                          >
                            {previewUrl ? (
                              <img
                                src={previewUrl}
                                alt={`${post.pageName} update ${mediaIndex + 1}`}
                                className="updates-post-media-image"
                              />
                            ) : (
                              <div className="updates-post-media-fallback">
                                <IonIcon icon={logoFacebook} />
                              </div>
                            )}

                            {isVideo && (
                              <div className="updates-post-video-overlay">
                                <span className="updates-post-video-badge">
                                  <IonIcon icon={playCircleOutline} />
                                  <span>Video</span>
                                </span>
                                <span className="updates-post-video-cta">
                                  {isLikelyPlayableVideoUrl(mediaItem.url) ? "Play video" : "Watch on Facebook"}
                                </span>
                              </div>
                            )}

                            {hasMultipleMedia && mediaIndex === 0 && (
                              <span className="updates-post-gallery-count">{postMedia.length} items</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="updates-empty-shell">
          <CityUpdatesContent compactCopy />
        </div>
      )}

      <IonModal
        isOpen={Boolean(galleryState)}
        onDidDismiss={() => {
          setGalleryState(null);
          setIsGalleryCaptionExpanded(false);
        }}
        className="updates-gallery-modal"
      >
        <IonHeader className="map-modal-header">
          <IonToolbar>
            <IonTitle>{galleryState?.pageName || "Update media"}</IonTitle>
            <IonButton slot="end" fill="clear" className="updates-gallery-close" onClick={() => setGalleryState(null)}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonToolbar>
        </IonHeader>
        <IonContent className="updates-gallery-content">
          {galleryState && (
            <div className="updates-gallery-shell">
              <Swiper
                modules={[Pagination]}
                pagination={{ clickable: true }}
                initialSlide={galleryState.initialIndex}
                onSwiper={(swiper: any) => {
                  swiperRef.current = swiper;
                }}
                onSlideChange={(swiper: any) => {
                  setGalleryState((current) =>
                    current
                      ? {
                          ...current,
                          initialIndex: swiper.activeIndex,
                        }
                      : current
                  );
                }}
                className="updates-gallery-swiper"
              >
                {galleryState.items.map((mediaItem, index) => {
                  const isPlayableVideo =
                    mediaItem.type === "video" &&
                    isLikelyPlayableVideoUrl(mediaItem.url);
                  const previewUrl = mediaItem.thumbnailUrl || mediaItem.url;
                  const canExpandCaption = shouldAllowCaptionExpand(galleryState.message);
                  const shouldShowCaption =
                    mediaItem.type !== "video" &&
                    Boolean(galleryState.message.trim());

                  return (
                    <SwiperSlide key={`${galleryState.postId}-gallery-${index}`}>
                      <div
                        className={`updates-gallery-slide ${shouldShowCaption ? "updates-gallery-slide-with-caption" : ""} ${
                          shouldShowCaption && isGalleryCaptionExpanded
                            ? "updates-gallery-slide-caption-open"
                            : ""
                        }`}
                      >
                        <div
                          className={`updates-gallery-media-frame ${
                            mediaItem.type === "video"
                              ? "updates-gallery-media-frame-video"
                              : "updates-gallery-media-frame-image"
                          }`}
                        >
                          {isPlayableVideo ? (
                            <video
                              className="updates-gallery-video"
                              controls
                              playsInline
                              preload="metadata"
                              poster={mediaItem.thumbnailUrl || undefined}
                            >
                              {mediaItem.url && <source src={mediaItem.url} />}
                            </video>
                          ) : previewUrl ? (
                            <img
                              src={previewUrl}
                              alt={`${galleryState.pageName} gallery item ${index + 1}`}
                              className="updates-gallery-image"
                            />
                          ) : (
                            <div className="updates-gallery-fallback">
                              <IonIcon icon={logoFacebook} />
                            </div>
                          )}

                          {shouldShowCaption && isGalleryCaptionExpanded && (
                            <div className="updates-gallery-caption updates-gallery-caption-expanded">
                              <p className="updates-gallery-caption-copy">
                                {renderLinkedText(galleryState.message)}
                              </p>
                              <button
                                type="button"
                                className="updates-gallery-caption-toggle"
                                onClick={() => setIsGalleryCaptionExpanded(false)}
                              >
                                See less
                              </button>
                            </div>
                          )}
                        </div>

                        {shouldShowCaption && !isGalleryCaptionExpanded && (
                          <div className="updates-gallery-caption-bar">
                            <p className="updates-gallery-caption-bar-copy">
                              {galleryState.message.trim()}
                            </p>
                            {canExpandCaption && (
                              <button
                                type="button"
                                className="updates-gallery-caption-toggle"
                                onClick={() => setIsGalleryCaptionExpanded(true)}
                              >
                                See more
                              </button>
                            )}
                          </div>
                        )}

                        {mediaItem.type === "video" && !isPlayableVideo && (
                          <a
                            className="updates-gallery-video-link"
                            href={mediaItem.url || galleryState.permalinkUrl || "#"}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <IonIcon icon={playCircleOutline} />
                            <span>Watch video on Facebook</span>
                          </a>
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
          )}
        </IonContent>
      </IonModal>
    </>
  );
});

CityUpdatesFeed.displayName = "CityUpdatesFeed";

export default CityUpdatesFeed;
